import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  StorageV2Service,
  UserV2Service,
  StoreV2,
  User,
  ToastDataV2,
  SI_UNITS,
  UnitsService,
  Unit,
  UnitSettingsV2Service,
  SelectItemV2,
  UnitType,
  ToastV2Service,
  GridFilterParamV2,
  UnitsV2,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable, catchError, forkJoin, of } from 'rxjs';
import { exhaustMap, filter, finalize, map } from 'rxjs/operators';
import { API_BASE_URL } from 'src/api/tokens';
import { SpinnerService } from './spinner/spinner.service';
import { ErrorHandlerService } from './error-handler/error-handler.service';
import {
  AutoCenterRequest,
  CalculateISOToleranceRequest,
  CalculateISOToleranceResponse,
  CoGFromBearingsRequest,
  OffsetBearingsFromCoGRequest,
  RotorTypeErrorResponse,
} from 'src/utils/interfaces/rotor-type.interfaces';
import { ToleranceAdapter } from './adapters/tolerances.adapter';
import { PlaneGeometryAdapter } from './adapters/plane-geometry.adapter';
import { TypeDataApi } from 'src/api/type-data.api';
import { first, isArray } from 'lodash';
import { DETAILS_NOTIFICATION_POSITION } from 'src/utils/constants/notification.const';
import { RotorTypeFormsData } from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import { UnitsHelperService } from './units-helper/units-helper.service';
import { EditorsService } from './editors.service';
import { MachineService } from './machine/machine.service';
import { RotorTypeConfiguration } from 'src/utils/constants/rotor-type.const';
import { mapToRotorTypeValue, mapToRotorTypeValueV2 } from './adapters/adapter.mappers';
import { RotorTypeSelections } from 'src/utils/enums/rotor-type.enum';

export interface RotorTypeName {
  id: string;
  name: string;
}

@Injectable()
export class TypeDataService extends StoreV2<RotorTypeName[]> {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly userService = inject(UserV2Service);
  private readonly spinnerService = inject(SpinnerService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly toleranceAdapter = inject(ToleranceAdapter);
  private readonly planeGeometryAdapter = inject(PlaneGeometryAdapter);
  private readonly unitsService = inject(UnitsService);
  private readonly unitSettings = inject(UnitSettingsV2Service);
  private readonly unitsHelperService = inject(UnitsHelperService);
  private readonly typeDataApi = inject(TypeDataApi);
  private readonly toastService = inject(ToastV2Service);
  private readonly editorsService = inject(EditorsService);
  private readonly machineService = inject(MachineService);

  private _uniqueNameError$ = new BehaviorSubject<boolean>(false);

  constructor() {
    const storageService = inject(StorageV2Service);

    super([], 'RotorTypeNameService', { storageService, name: 'RotorTypeNameService' });

    this.userService.currentUser$
      .pipe(filter((user: User) => !!user))
      .subscribe(() => this.getRotorTypeNames$());
  }

  public getRotorTypeNamesForFilter$(): Observable<string[]> {
    return this.state$.pipe(
      map((rotorTypeNames) => rotorTypeNames.map((rotorTypeName) => rotorTypeName.name))
    );
  }

  public getIdFromRotorTypeName(name: string): string {
    const rotorTypeName = this.state.find((_rotorTypeName) => _rotorTypeName.name === name);
    return rotorTypeName ? rotorTypeName.id : '';
  }

  public getRotorTypeNames$(): void {
    // Turn on loading spinner, because the Names are needed for Rotortype Name validation
    this.spinnerService.setLoading(true);
    this.http
      .get<{ data: RotorTypeName[] }>(`${this.baseUrl}/TypeData/Names`)
      .pipe(
        catchError((error) => {
          this.errorHandlerService.setAppError(true);
          throw error;
        }),
        finalize(() => this.spinnerService.setLoading(false))
      )
      .subscribe((rotorTypeNames) => {
        this.setState('get rotor type names', rotorTypeNames.data);
      });
  }

  public uniqueNameError$(): Observable<boolean> {
    return this._uniqueNameError$.asObservable();
  }

  public setUniqueNameError(value: boolean): void {
    this._uniqueNameError$.next(value);
  }

  /**
   * Effect to handle the calculation of ISO tolerances.
   * This effect is triggered when the calculateISOTolerances action is dispatched.
   * It performs unit conversions, prepares the request, calls the API, and updates the store with the result.
   */
  public calculateIsoTolerances$({
    selectedTolerancePlane,
    data,
    units,
    geometry,
  }: {
    selectedTolerancePlane?: SelectItemV2;
    data: RotorTypeFormsData;
    units: UnitsV2[] | number;
    geometry?: any;
  }): Observable<CalculateISOToleranceResponse> {
    return this.getConvertedData$(data).pipe(
      map((convertedData) => {
        const request: CalculateISOToleranceRequest = {
          selectedTolerancePlane: selectedTolerancePlane?.value as number,
          iso21940Tolerance: this.toleranceAdapter.adapt(
            {
              ...data,
              tolerancesStep: {
                ...data.tolerancesStep,
                isoCalculation: {
                  ...data.tolerancesStep.isoCalculation,
                  ...convertedData.convertedIsoCalculationData,
                  ...(data.supportSettingsStep.selectedCorrectionPlane !==
                    RotorTypeSelections.Static && {
                    deviation: data.tolerancesStep.isoCalculation.deviation / 100,
                    centerGravity1: {
                      value: geometry?.offsetBearing1,
                      unit: SI_UNITS.length,
                    },
                    centerGravity2: {
                      value: geometry?.offsetBearing2,
                      unit: SI_UNITS.length,
                    },
                  }),
                },
              },
            },
            units as UnitsV2[]
          ).iso21940Tolerance,
          rotorPlaneGeometry: this.planeGeometryAdapter.adapt(
            {
              ...data,
              rotorTypeSettingsStep: {
                ...data.rotorTypeSettingsStep,
                ...(data.supportSettingsStep.selectedCorrectionPlane !==
                  RotorTypeSelections.Static && {
                  a: {
                    ...data.rotorTypeSettingsStep.a,
                    value: geometry.a, // it is required to pass the geometry values to the adapter and not take the value from the data, because we need them in SI Units
                    unit: SI_UNITS.length,
                  },
                  b: {
                    ...data.rotorTypeSettingsStep.b,
                    value: geometry.b,
                    unit: SI_UNITS.length,
                  },
                  c: {
                    ...data.rotorTypeSettingsStep.c,
                    value: geometry.c,
                    unit: SI_UNITS.length,
                  },
                }),
              },
            },
            units as UnitsV2[]
          ),
          targetUnbalanceUnit: this.unitsService.getUnitIndex(
            data.tolerancesStep.isoCalculation.selectedUnbalanceUnit.value as Unit,
            'Unbalance'
          ),
          decimalDigits: this.unitSettings.state?.units?.decimals,
        };
        return { request, data };
      }),
      exhaustMap(({ request }) => {
        return this.typeDataApi.calculateIsoTolerance$(request).pipe(
          filter((resp) => !!resp),
          catchError((error: HttpErrorResponse) => {
            this.handleCreateIsoToleranceError(error);
            throw error;
          })
        );
      })
    );
  }

  public getBearingOffsets$(
    supportCase: number,
    a: number,
    b: number,
    c: number,
    cog: number,
    selectedTolerancePlane: SelectItemV2,
    units: UnitsV2[]
  ) {
    const supportCaseIndex = RotorTypeConfiguration[supportCase - 1].index;
    const calculateOffsetBearingRequest: OffsetBearingsFromCoGRequest = {
      a: mapToRotorTypeValue({ name: 'a', value: a, unit: 'm' }, units),
      b: mapToRotorTypeValue({ name: 'b', value: b, unit: 'm' }, units),
      c: mapToRotorTypeValue({ name: 'c', value: c, unit: 'm' }, units),
      centerOfGravity: mapToRotorTypeValue({ name: 'cog', value: cog, unit: 'm' }, units),
      supportCase: mapToRotorTypeValueV2({ value: supportCaseIndex, name: 'supportCase' }),
      selectedTolerancePlane: mapToRotorTypeValueV2(selectedTolerancePlane),
    };

    return this.typeDataApi.getBearingOffsets$(calculateOffsetBearingRequest);
  }

  public getCoG$(params: {
    supportCaseIndex: number;
    a: number;
    b: number;
    c: number;
    offset1: number;
    offset2: number;
    selectedTolerancePlane: number;
    units: UnitsV2[];
  }) {
    const cogRequest: CoGFromBearingsRequest = {
      a: mapToRotorTypeValue({ name: 'a', value: params.a, unit: 'm' }, params.units),
      b: mapToRotorTypeValue({ name: 'b', value: params.b, unit: 'm' }, params.units),
      c: mapToRotorTypeValue({ name: 'c', value: params.c, unit: 'm' }, params.units),
      offsetBearing1ToCenterGravity: mapToRotorTypeValue(
        { name: 'offset1', value: params.offset1, unit: 'm' },
        params.units
      ),
      offsetBearing2ToCenterGravity: mapToRotorTypeValue(
        { name: 'offset2', value: params.offset2, unit: 'm' },
        params.units
      ),
      supportCase: mapToRotorTypeValueV2({ value: params.supportCaseIndex, name: 'supportCase' }),
      selectedTolerancePlane: mapToRotorTypeValueV2({
        value: params.selectedTolerancePlane,
        name: 'selectedTolerancePlane',
      }),
    };

    return this.typeDataApi.getCoG$(cogRequest);
  }

  public getAutoCenter$(
    supportCase: number,
    a: number,
    b: number,
    c: number,
    selectedTolerancePlane: SelectItemV2,
    units: UnitsV2[]
  ) {
    const supportCaseIndex = RotorTypeConfiguration[supportCase - 1].index;
    const autoCenterRequest: AutoCenterRequest = {
      a: mapToRotorTypeValue({ name: 'a', value: a, unit: 'm' }, units),
      b: mapToRotorTypeValue({ name: 'b', value: b, unit: 'm' }, units),
      c: mapToRotorTypeValue({ name: 'c', value: c, unit: 'm' }, units),
      supportCase: mapToRotorTypeValueV2({ value: supportCaseIndex, name: 'supportCase' }),
      selectedTolerancePlane: mapToRotorTypeValueV2(selectedTolerancePlane),
    };

    return this.typeDataApi.getAutoCenter$(autoCenterRequest);
  }

  public getRotorTypeNames(filterValues: string[]): string[] {
    return filterValues.map((val) =>
      this.getIdFromRotorTypeName(val) ? 'RotorTypeIds=' + this.getIdFromRotorTypeName(val) : null
    );
  }

  public createFilterURL(filter: GridFilterParamV2[]): string {
    const urlActions = {
      RotorTypeName: (value: string[]): string => `RotorTypeName=${value.join()}&`,
      name: (value: string[]): string => `${this.getRotorTypeNames(value).join('&')}&`,
      machineName: (value: string[]): string => `${this.getMachineIds(value).join('&')}&`,
      status: (value: string[]): string => `${this.getStatuses(value).join('&')}&`,
      syncState: (value: string[]): string => `${this.getSyncStates(value).join('&')}&`,
      createdBy: (value: string[]): string => `CreatedByUserIds=${this.getUserIds(value)}&`,
      modifiedBy: (value: string[]): string => `ModifiedByUserIds=${this.getUserIds(value)}&`,
      modified: (value: any): string => this.getStringFromDateRange(value, 'Modified'),
      created: (value: any): string => this.getStringFromDateRange(value, 'Created'),
    };
    const url: string = filter.reduce(
      (acc, { key, value }: GridFilterParamV2) => acc + urlActions[key](value) || '',
      ''
    );

    return `${this.typeDataApi.typeDataBaseUrl}?${url}`;
  }

  public getMachineIds(filterValues: string[]): string[] {
    // Here we need to work with all machines, not only the available ones
    const machines = this.machineService.state;
    return filterValues.map((val) => {
      const machineId = Object.keys(machines).find((m) => machines[m].name === val);
      return 'MachineIds=' + machines[machineId].assetId;
    });
  }

  public getStatuses(filterValues: string[]): string[] {
    return filterValues.map((val) => `Statuses=${val}`);
  }

  public getSyncStates(filterValues: string[]): string[] {
    return filterValues.map((val) => `SyncStates=${val}`);
  }

  public getUserIds(filterValues: string[]): string[] {
    return filterValues.map((val) => this.editorsService.getIdFromUserName(val));
  }

  public getStringFromDateRange(value: any, key: string): string {
    const toISOStringWithoutTimezone = (date: string) => {
      const _date = new Date(date);
      const tzOffset = _date.getTimezoneOffset();
      return new Date(_date.getTime() - tzOffset * 60 * 1000).toISOString();
    };

    return `${key}Start=${toISOStringWithoutTimezone(
      value.from
    )}&${key}End=${toISOStringWithoutTimezone(value.to)}&`;
  }

  private handleCreateIsoToleranceError(error: HttpErrorResponse) {
    let err: RotorTypeErrorResponse = {
      message: error?.message,
      params: { ['error']: [error.error] },
    };
    if (
      error.error?.params &&
      typeof error.error.params === 'object' &&
      isArray(first(Object.values(error.error.params)))
    ) {
      this.showErrorToast(
        {
          message: first(first(Object.values(error.error.params))),
        },
        DETAILS_NOTIFICATION_POSITION
      );
    } else {
      this.showErrorToast(err, DETAILS_NOTIFICATION_POSITION);
    }
  }

  private getConvertedData$(data: RotorTypeFormsData): Observable<any> {
    // Always convert these values
    const convertObservables = {
      rotorMass: this.getConvertedResult$(data.tolerancesStep.isoCalculation.rotorMass, 'Mass'),
      nominalSpeed: this.getConvertedResult$(
        data.tolerancesStep.isoCalculation.nominalSpeed,
        'Frequency'
      ),
    };

    return forkJoin(convertObservables).pipe(
      map((convertedValues: any) => {
        const result: any = {
          convertedIsoCalculationData: {
            rotorMass: {
              ...data.tolerancesStep.isoCalculation.rotorMass,
              value: convertedValues.rotorMass,
              unit: SI_UNITS.mass,
            },
            nominalSpeed: {
              ...data.tolerancesStep.isoCalculation.nominalSpeed,
              value: convertedValues.nominalSpeed,
              unit: SI_UNITS.frequency,
            },
          },
        };

        return result;
      })
    );
  }
  /**
   * Converts a single value to its SI unit equivalent if necessary.
   * @param item - The SelectItemV2 object containing the value to convert.
   * @param unitType - The type of unit (e.g., 'Length', 'Mass').
   * @returns An Observable of the converted numeric value.
   */
  private getConvertedResult$(item: SelectItemV2, unitType: UnitType): Observable<number> {
    if (!item) {
      console.warn(`No item provided for unitType: ${unitType}. Returning null.`);
      return of(null);
    }

    const SIUnit = this.unitsService.getSItUnitFromUnitType(unitType);
    const convert = item.unit !== SIUnit;

    if (convert) {
      return this.unitsHelperService.convertUnit$(item as SelectItemV2<number>, unitType, SIUnit);
    } else {
      return of(item.value as number);
    }
  }

  private showErrorToast(error: RotorTypeErrorResponse, toastPosition: string): void {
    let text, position;

    if (error.params) {
      text = Object.values(error?.params)
        .map(([item]: string[]) => item)
        .join('\n');
      position = `calc(${toastPosition} - ${16 * (Object.values(error?.params).length - 1)}px)`;
    } else if (error.message) {
      text = error.message;
    }

    this.toastService.open(<ToastDataV2>{
      text,
      position,
      type: 'error',
      title: 'Error',
    });
  }
}
